import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  Plus, 
  Trash2, 
  Save, 
  Download, 
  Calculator,
  TrendingUp,
  Target,
  Award
} from "lucide-react";
import NavBar from "@/components/NavBar";

interface QualitativeCriteria {
  id: string;
  name: string;
  description: string;
  weight: number;
  scale: 'likert5' | 'likert7' | 'percentage' | 'binary';
}

interface Option {
  id: string;
  name: string;
  description: string;
  scores: { [criteriaId: string]: number };
  notes: string;
}

export default function QualitativeAssessment() {
  const [criteria, setCriteria] = useState<QualitativeCriteria[]>([
    {
      id: "1",
      name: "Strategic Alignment",
      description: "How well does this option align with organizational strategy?",
      weight: 25,
      scale: 'likert5'
    },
    {
      id: "2", 
      name: "Implementation Feasibility",
      description: "How feasible is it to implement this option?",
      weight: 20,
      scale: 'likert5'
    },
    {
      id: "3",
      name: "Stakeholder Acceptance",
      description: "Level of stakeholder buy-in and acceptance",
      weight: 15,
      scale: 'likert5'
    },
    {
      id: "4",
      name: "Risk Level",
      description: "Overall risk assessment (lower score = higher risk)",
      weight: 20,
      scale: 'likert5'
    },
    {
      id: "5",
      name: "Innovation Potential",
      description: "Potential for innovation and improvement",
      weight: 20,
      scale: 'likert5'
    }
  ]);

  const [options, setOptions] = useState<Option[]>([
    {
      id: "1",
      name: "Do Nothing (Baseline)",
      description: "Maintain current state",
      scores: { "1": 2, "2": 5, "3": 3, "4": 3, "5": 1 },
      notes: "Low risk but limited strategic value"
    }
  ]);

  const [newCriteria, setNewCriteria] = useState({
    name: "",
    description: "",
    weight: 10,
    scale: 'likert5' as const
  });

  const [newOption, setNewOption] = useState({
    name: "",
    description: "",
    notes: ""
  });

  const addCriteria = () => {
    if (newCriteria.name && newCriteria.description) {
      const criteria_item: QualitativeCriteria = {
        id: Date.now().toString(),
        name: newCriteria.name,
        description: newCriteria.description,
        weight: newCriteria.weight,
        scale: newCriteria.scale
      };
      setCriteria([...criteria, criteria_item]);
      setNewCriteria({ name: "", description: "", weight: 10, scale: 'likert5' });
    }
  };

  const addOption = () => {
    if (newOption.name && newOption.description) {
      const defaultScores: { [key: string]: number } = {};
      criteria.forEach(c => {
        defaultScores[c.id] = 3; // Default middle score
      });

      const option: Option = {
        id: Date.now().toString(),
        name: newOption.name,
        description: newOption.description,
        scores: defaultScores,
        notes: newOption.notes
      };
      setOptions([...options, option]);
      setNewOption({ name: "", description: "", notes: "" });
    }
  };

  const updateScore = (optionId: string, criteriaId: string, score: number) => {
    setOptions(options.map(option => 
      option.id === optionId 
        ? { 
            ...option, 
            scores: { ...option.scores, [criteriaId]: score }
          }
        : option
    ));
  };

  const calculateWeightedScore = (option: Option) => {
    const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);
    const weightedSum = criteria.reduce((sum, c) => {
      const score = option.scores[c.id] || 0;
      const maxScore = getMaxScore(c.scale);
      const normalizedScore = (score / maxScore) * 100;
      return sum + (normalizedScore * c.weight);
    }, 0);
    return totalWeight > 0 ? weightedSum / totalWeight : 0;
  };

  const getMaxScore = (scale: string) => {
    switch (scale) {
      case 'likert5': return 5;
      case 'likert7': return 7;
      case 'percentage': return 100;
      case 'binary': return 1;
      default: return 5;
    }
  };

  const getScaleLabel = (scale: string, score: number) => {
    switch (scale) {
      case 'likert5':
        const labels5 = ['Poor', 'Below Average', 'Average', 'Good', 'Excellent'];
        return labels5[score - 1] || 'Not Rated';
      case 'likert7':
        const labels7 = ['Very Poor', 'Poor', 'Below Average', 'Average', 'Above Average', 'Good', 'Excellent'];
        return labels7[score - 1] || 'Not Rated';
      case 'percentage':
        return `${score}%`;
      case 'binary':
        return score === 1 ? 'Yes' : 'No';
      default:
        return score.toString();
    }
  };

  const getBestOption = () => {
    if (options.length === 0) return null;
    return options.reduce((best, current) => 
      calculateWeightedScore(current) > calculateWeightedScore(best) ? current : best
    );
  };

  const normalizeWeights = () => {
    const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);
    if (totalWeight !== 100) {
      setCriteria(criteria.map(c => ({
        ...c,
        weight: Math.round((c.weight / totalWeight) * 100)
      })));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Qualitative Assessment</h1>
            <p className="text-muted-foreground">
              Multi-criteria decision analysis with weighted scoring methodology
            </p>
          </div>

          <Tabs defaultValue="criteria" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="criteria">Assessment Criteria</TabsTrigger>
              <TabsTrigger value="scoring">Score Options</TabsTrigger>
              <TabsTrigger value="results">Results & Analysis</TabsTrigger>
              <TabsTrigger value="reports">Reports</TabsTrigger>
            </TabsList>

            <TabsContent value="criteria" className="space-y-6">
              {/* Add New Criteria */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    Add Assessment Criteria
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      placeholder="Criteria name"
                      value={newCriteria.name}
                      onChange={(e) => setNewCriteria({ ...newCriteria, name: e.target.value })}
                    />
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Weight %"
                        value={newCriteria.weight}
                        onChange={(e) => setNewCriteria({ ...newCriteria, weight: Number(e.target.value) })}
                        className="w-24"
                      />
                      <select
                        value={newCriteria.scale}
                        onChange={(e) => setNewCriteria({ ...newCriteria, scale: e.target.value as any })}
                        className="border rounded px-3 py-2"
                      >
                        <option value="likert5">1-5 Scale</option>
                        <option value="likert7">1-7 Scale</option>
                        <option value="percentage">0-100%</option>
                        <option value="binary">Yes/No</option>
                      </select>
                    </div>
                  </div>
                  <Textarea
                    placeholder="Criteria description"
                    value={newCriteria.description}
                    onChange={(e) => setNewCriteria({ ...newCriteria, description: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <Button onClick={addCriteria} disabled={!newCriteria.name || !newCriteria.description}>
                      Add Criteria
                    </Button>
                    <Button variant="outline" onClick={normalizeWeights}>
                      <Calculator className="h-4 w-4 mr-2" />
                      Normalize Weights
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Existing Criteria */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Assessment Criteria</h3>
                  <Badge variant="outline">
                    Total Weight: {criteria.reduce((sum, c) => sum + c.weight, 0)}%
                  </Badge>
                </div>
                
                {criteria.map((criterion) => (
                  <Card key={criterion.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base">{criterion.name}</CardTitle>
                          <CardDescription>{criterion.description}</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge>{criterion.weight}%</Badge>
                          <Badge variant="outline">{criterion.scale}</Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCriteria(criteria.filter(c => c.id !== criterion.id))}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="scoring" className="space-y-6">
              {/* Add New Option */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    Add Option to Assess
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      placeholder="Option name"
                      value={newOption.name}
                      onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
                    />
                    <Textarea
                      placeholder="Option description"
                      value={newOption.description}
                      onChange={(e) => setNewOption({ ...newOption, description: e.target.value })}
                    />
                  </div>
                  <Button onClick={addOption} disabled={!newOption.name || !newOption.description}>
                    Add Option
                  </Button>
                </CardContent>
              </Card>

              {/* Score Options */}
              <div className="space-y-4">
                {options.map((option) => (
                  <Card key={option.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base">{option.name}</CardTitle>
                          <CardDescription>{option.description}</CardDescription>
                        </div>
                        <Badge className="bg-primary">
                          Score: {calculateWeightedScore(option).toFixed(1)}%
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4">
                        {criteria.map((criterion) => (
                          <div key={criterion.id} className="space-y-2">
                            <label className="text-sm font-medium">
                              {criterion.name} ({criterion.weight}%)
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="range"
                                min="1"
                                max={getMaxScore(criterion.scale)}
                                value={option.scores[criterion.id] || 1}
                                onChange={(e) => updateScore(option.id, criterion.id, Number(e.target.value))}
                                className="flex-1"
                              />
                              <Badge variant="outline" className="min-w-16">
                                {getScaleLabel(criterion.scale, option.scores[criterion.id] || 1)}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="results" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="h-5 w-5" />
                      Ranking Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {options
                        .sort((a, b) => calculateWeightedScore(b) - calculateWeightedScore(a))
                        .map((option, index) => (
                          <div key={option.id} className="flex items-center justify-between p-3 border rounded">
                            <div className="flex items-center gap-3">
                              <Badge variant={index === 0 ? "default" : "outline"}>
                                #{index + 1}
                              </Badge>
                              <div>
                                <div className="font-medium">{option.name}</div>
                                <div className="text-sm text-muted-foreground">
                                  {calculateWeightedScore(option).toFixed(1)}% overall score
                                </div>
                              </div>
                            </div>
                            <Progress 
                              value={calculateWeightedScore(option)} 
                              className="w-24"
                            />
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Recommended Option
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {getBestOption() && (
                      <div className="space-y-3">
                        <div>
                          <h3 className="font-semibold text-lg">{getBestOption()!.name}</h3>
                          <p className="text-muted-foreground">{getBestOption()!.description}</p>
                        </div>
                        <Badge className="bg-green-600">
                          {calculateWeightedScore(getBestOption()!).toFixed(1)}% Total Score
                        </Badge>
                        <div className="space-y-2">
                          <h4 className="font-medium">Criteria Breakdown:</h4>
                          {criteria.map((criterion) => {
                            const score = getBestOption()!.scores[criterion.id] || 0;
                            const maxScore = getMaxScore(criterion.scale);
                            const percentage = (score / maxScore) * 100;
                            return (
                              <div key={criterion.id} className="flex items-center justify-between text-sm">
                                <span>{criterion.name}</span>
                                <div className="flex items-center gap-2">
                                  <Progress value={percentage} className="w-16" />
                                  <span>{getScaleLabel(criterion.scale, score)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="reports" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Export Options</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button className="w-full" variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Export Detailed Report
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Save className="h-4 w-4 mr-2" />
                      Save Assessment Template
                    </Button>
                    <Button className="w-full" variant="outline">
                      <Calculator className="h-4 w-4 mr-2" />
                      Export Score Matrix
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Assessment Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div>Total Criteria: {criteria.length}</div>
                      <div>Options Assessed: {options.length}</div>
                      <div>Weight Distribution: 
                        {criteria.reduce((sum, c) => sum + c.weight, 0) === 100 ? " ✓ Balanced" : " ⚠ Unbalanced"}
                      </div>
                      {getBestOption() && (
                        <div>Recommended: {getBestOption()!.name}</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
